import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { FiDownload, FiArrowRight, FiGithub, FiLinkedin, FiMail } from 'react-icons/fi';
import SplitText from './SplitText';
import MagneticButtonComponent from './MagneticButton';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: transparent;
  overflow: hidden;
  z-index: 1;
`;

const FloatingCard = styled(motion.div)`
  position: absolute;
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 20px;
  backdrop-filter: blur(10px);
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #00ff00;
  font-size: 0.9rem;
  font-weight: 500;
  z-index: 1;
  box-shadow: 0 4px 15px rgba(0, 255, 0, 0.1);
  
  &:nth-child(1) {
    top: 20%;
    left: 10%;
  }
  
  &:nth-child(2) {
    top: 60%;
    right: 15%;
  }
  
  &:nth-child(3) {
    bottom: 30%;
    left: 20%;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    padding: 0 1rem;
    margin-top: 2rem;
  }
  
  @media (max-width: 480px) {
    padding: 0 0.8rem;
    margin-top: 1rem;
  }
`;

const Greeting = styled(motion.h1)`
  font-size: clamp(2rem, 6vw, 4rem);
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(45deg, #ffffff, #00ff00, #3b82f6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const AnimatedText = styled(motion.h2)`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 600;
  margin-bottom: 1.5rem;
  color: #00ff00;
  min-height: 3rem;
`;

const Description = styled(motion.p)`
  font-size: 1.2rem;
  color: #e2e8f0;
  margin-bottom: 2.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.7;
    margin-bottom: 2rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
    line-height: 1.8;
    margin-bottom: 1.5rem;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
    margin-bottom: 2.5rem;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    margin-bottom: 2rem;
  }
`;

const SecondaryButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: transparent;
  color: #00ff00;
  padding: 1rem 2rem;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 600;
  border: 2px solid #00ff00;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #00ff00;
    color: #000;
    transform: translateY(-2px);
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 0.8rem;
  }
  
  @media (max-width: 480px) {
    gap: 0.6rem;
  }
`;

const SocialLink = styled(motion.a)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(0, 255, 0, 0.3);
  border-radius: 50%;
  color: #00ff00;
  text-decoration: none;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background: linear-gradient(135deg, #00ff00 0%, #3b82f6 100%);
    color: #000;
    transform: translateY(-3px);
    box-shadow: 0 10px 25px rgba(0, 255, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
  
  @media (max-width: 480px) {
    width: 40px;
    height: 40px;
  }
`;

const Hero = () => {
  const [currentText, setCurrentText] = useState(0);
  const controls = useAnimation();
  
  const texts = [
    "Web Developer",
    "MERN Stack Developer",
    "AI Solutions Developer",
    "Full Stack Developer"
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentText((prev) => (prev + 1) % texts.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [texts.length]);

  useEffect(() => {
    controls.start({
      opacity: [0, 1, 1, 0],
      transition: { duration: 2, ease: "easeInOut" }
    });
  }, [currentText, controls]);

  const floatingCards = [
    { text: "React", icon: "⚛️" },
    { text: "Node.js", icon: "🟢" },
    { text: "AI/ML", icon: "🤖" }
  ];

  return (
    <HeroSection id="home">
      {/* Floating Animation Cards */}
      {floatingCards.map((card, index) => (
        <FloatingCard
          key={index}
          initial={{ opacity: 0, y: 20 }}
          animate={{ 
            opacity: [0.3, 0.8, 0.3],
            y: [0, -10, 0],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 3 + index,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <span>{card.icon}</span>
          {card.text}
        </FloatingCard>
      ))}

      <Container>
        <Greeting
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <SplitText type="words" animation="slideUp" stagger={0.1}>
            Hello, I'm Anas Naveed Butt
          </SplitText>
        </Greeting>
        
        <AnimatedText
          key={currentText}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.5 }}
        >
          <SplitText type="characters" animation="slideUp" stagger={0.05}>
            {texts[currentText]}
          </SplitText>
        </AnimatedText>
        
        <Description
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <SplitText type="words" animation="fade" stagger={0.1}>
            I'm a driven and adaptable Computer Science student specializing in web development and exploring cutting-edge fields in machine learning, large language models (LLMs), and artificial intelligence. Skilled in creating efficient, user-focused applications and passionate about integrating AI innovations into modern software solutions.
          </SplitText>
        </Description>
        
        <ButtonGroup
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <MagneticButtonComponent
            onClick={() => {
              try {
                const link = document.createElement('a');
                link.href = '/Resume_Anas.pdf';
                link.download = 'Anas_Butt_Resume.pdf';
                link.target = '_blank';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } catch (error) {
                console.error('Download failed:', error);
                // Fallback: open in new tab
                window.open('/Resume_Anas.pdf', '_blank');
              }
            }}
            data-cursor-text="Download Resume"
            className="cursor-target"
          >
            <FiDownload style={{ marginRight: '0.5rem' }} />
            Download Resume
          </MagneticButtonComponent>
          
          <SecondaryButton
            onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            data-cursor-text="Learn More"
            className="cursor-target"
          >
            Learn More
            <FiArrowRight />
          </SecondaryButton>
        </ButtonGroup>
        
        <SocialLinks
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <SocialLink
            href="https://github.com/Anasnaveed69"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            data-cursor-text="GitHub"
            className="cursor-target"
          >
            <FiGithub size={20} />
          </SocialLink>
          
          <SocialLink
            href="https://www.linkedin.com/in/anas-butt-8661992aa/"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            data-cursor-text="LinkedIn"
            className="cursor-target"
          >
            <FiLinkedin size={20} />
          </SocialLink>
          
          <SocialLink
            href="mailto:buttanas813@gmail.com"
            whileHover={{ scale: 1.1, rotate: 5 }}
            whileTap={{ scale: 0.95 }}
            data-cursor-text="Email"
            className="cursor-target"
          >
            <FiMail size={20} />
          </SocialLink>
        </SocialLinks>
      </Container>
    </HeroSection>
  );
};

export default Hero;

